<template>
  <BaseForm ref="formComponent" :method="onFormSubmit" :reset-on-submit="false">
    <div class="text-gray">
      <div class="space-y-6">
        <div class="text-gray flex flex-col gap-1">
          <div class="font-bold">Type of participation</div>
          <div class="text-sm text-gray-600 md:text-gray">
            Please choose preferred type of participation for your game*
          </div>
        </div>

        <BaseRadioGroup
          v-model="selection"
          :options="options"
          name="participation"
          required
          :disabled="props.disabled"
        />
      </div>
    </div>
  </BaseForm>
</template>

<script setup lang="ts">
import BaseForm from '~/components/base/BaseForm.vue'
import type { OptionInterface } from '~/components/base/controls/use-simple-input'

interface Props {
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const emit = defineEmits<{
  (e: 'submitRequest', value: void): void
}>()

defineExpose({
  validate,
  getParticipationData,
})

const formComponent = ref<InstanceType<typeof BaseForm> | null>(null)

enum ParticipationType {
  OnSite = 'onSite',
  Contest = 'contest',
  Both = 'both',
}

export interface ParticipationData {
  label: string
  isOnSite: boolean
  isContestParticipant: boolean
}

const ParticipationList: Record<ParticipationType, ParticipationData> = {
  [ParticipationType.OnSite]: {
    label: 'On-site showcase',
    isOnSite: true,
    isContestParticipant: false,
  },
  [ParticipationType.Contest]: {
    label: 'WN Game Finder Contest',
    isOnSite: false,
    isContestParticipant: true,
  },
  [ParticipationType.Both]: {
    label: 'Both on-site showcase and WN Game Finder Contest',
    isOnSite: true,
    isContestParticipant: true,
  },
}

const selection = ref<ParticipationType | null>(null)

const options: OptionInterface[] = Object.entries(ParticipationList).map(
  ([key, participation]) => ({
    label: participation.label,
    value: key,
  })
)

function onFormSubmit(): void {
  emit('submitRequest')
}

async function validate(): Promise<boolean> {
  return (await formComponent.value?.validate())?.valid || false
}

function getParticipationData(): ParticipationData | null {
  return selection.value && ParticipationList[selection.value]
}
</script>
